import {
  BarChart3,
  Download,
  FolderOpen,
  PanelLeft,
  PanelRight,
  Plus,
  RotateCcw,
  ShieldCheck,
  Undo2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChannelBrowser } from './components/ChannelBrowser';
import { FileDragOverlay } from './components/FileDragOverlay';
import { FileDropZone } from './components/FileDropZone';
import { GraphPanel } from './components/GraphPanel';
import { Inspector } from './components/Inspector';
import { OverviewScrubber } from './components/OverviewScrubber';
import { sortChannelsByName } from './lib/channels';
import { formatSessionSummary } from './lib/sessionSummary';
import {
  CloudDatalogError,
  downloadCloudDatalog,
  parseCloudDatalogRoute,
} from './services/cloudDatalogClient';
import { DatalogClientError, datalogClient } from './services/datalogClient';
import { useWorkspaceStore } from './store/workspace';
import type { ChannelDefinition } from './types';
import defaultDatalogUrl from '../Datalogs/Log001_Sample_Data.csv?url';

type TimeMapping = {
  file: File;
  candidates: ChannelDefinition[];
};

function GaugeMark() {
  return (
    <div className="gauge-mark" aria-hidden="true">
      <span className="gauge-arc" />
      <span className="gauge-red" />
      <span className="gauge-needle" />
    </div>
  );
}

function BanksLogo() {
  return <img className="banks-logo" src="/banks-logo.png" alt="Banks" />;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function App() {
  const session = useWorkspaceStore((state) => state.session);
  const graphs = useWorkspaceStore((state) => state.graphs);
  const focusedGraphId = useWorkspaceStore((state) => state.focusedGraphId);
  const savedWorkspaces = useWorkspaceStore((state) => state.savedWorkspaces);
  const undoStack = useWorkspaceStore((state) => state.undoStack);
  const inspectorOpen = useWorkspaceStore((state) => state.inspectorOpen);
  const openSession = useWorkspaceStore((state) => state.openSession);
  const addGraph = useWorkspaceStore((state) => state.addGraph);
  const assignChannel = useWorkspaceStore((state) => state.assignChannel);
  const setInspectorOpen = useWorkspaceStore((state) => state.setInspectorOpen);
  const undo = useWorkspaceStore((state) => state.undo);
  const resetWorkspace = useWorkspaceStore((state) => state.resetWorkspace);
  const inputRef = useRef<HTMLInputElement>(null);
  const defaultDatalogStarted = useRef(false);
  const cloudDownloadController = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState<'download' | 'parse'>('parse');
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('Reading datalog');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [channelDragActive, setChannelDragActive] = useState(false);
  const [channelsCollapsed, setChannelsCollapsed] = useState(false);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const [timeMapping, setTimeMapping] = useState<TimeMapping | null>(null);
  const [selectedTimeColumn, setSelectedTimeColumn] = useState<number | null>(null);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 3200);
    return () => clearTimeout(timer);
  }, [notice]);

  const importSequence = useRef(0);
  const importFile = async (file: File, timeColumnIndex?: number) => {
    const sequence = (importSequence.current += 1);
    const isCurrent = () => sequence === importSequence.current;
    setLoading(true);
    setLoadingMode('parse');
    setProgress(0);
    setPhase('Reading datalog');
    setError(null);
    setTimeMapping(null);
    try {
      const metadata = await datalogClient.loadFile(
        file,
        timeColumnIndex === undefined ? undefined : { timeColumnIndex },
        (nextProgress, nextPhase) => {
          setProgress(nextProgress);
          setPhase(nextPhase);
        },
      );
      if (!isCurrent()) return;
      openSession(metadata);
      setProgress(1);
      setNotice(
        `${metadata.sampleCount.toLocaleString()} samples · ${metadata.channels.length} channels · processed locally`,
      );
    } catch (caught) {
      if (!isCurrent()) return;
      if (caught instanceof DatalogClientError && caught.code === 'TIME_COLUMN_REQUIRED' && caught.candidates) {
        setTimeMapping({ file, candidates: caught.candidates });
        setSelectedTimeColumn(caught.candidates[0]?.sourceColumnIndex ?? null);
      } else if (!(caught instanceof DatalogClientError && caught.code === 'CANCELED')) {
        setLoadFailure(caught instanceof Error ? caught.message : 'The datalog could not be opened.');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const importLocalFile = (file: File) => {
    const route = parseCloudDatalogRoute(window.location.pathname);
    if (route.kind !== 'none') window.history.replaceState(null, '', '/');
    void importFile(file);
  };

  useEffect(() => {
    if (defaultDatalogStarted.current || session) return;
    defaultDatalogStarted.current = true;
    const route = parseCloudDatalogRoute(window.location.pathname);
    if (route.kind === 'invalid') {
      setError('This cloud datalog link is invalid. Open a local iDash CSV instead.');
      return;
    }
    if (route.kind === 'datalog') {
      const sequenceAtStart = importSequence.current;
      const controller = new AbortController();
      cloudDownloadController.current = controller;
      setLoading(true);
      setLoadingMode('download');
      setProgress(0);
      setPhase('Downloading cloud datalog');
      void downloadCloudDatalog(route.uploadId, {
        signal: controller.signal,
        onProgress: setProgress,
      })
        .then(async (file) => {
          cloudDownloadController.current = null;
          if (importSequence.current === sequenceAtStart) await importFile(file);
        })
        .catch((caught) => {
          if (!(caught instanceof CloudDatalogError && caught.code === 'CANCELED')) {
            setError(caught instanceof Error ? caught.message : 'The cloud datalog could not be opened.');
          }
        })
        .finally(() => {
          cloudDownloadController.current = null;
          setLoading(false);
        });
      return;
    }
    // If the user opens a file while this fetch is in flight, the sample must not replace it.
    const sequenceAtStart = importSequence.current;
    void fetch(defaultDatalogUrl)
      .then(async (response) => {
        if (!response.ok) throw new Error('The default datalog could not be loaded.');
        const blob = await response.blob();
        return new File([blob], 'Sample Data.csv', { type: 'text/csv' });
      })
      .then((file) => {
        if (importSequence.current === sequenceAtStart) return importFile(file);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : 'The default datalog could not be loaded.'));
  }, []);

  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  const importLocalFileRef = useRef(importLocalFile);
  importLocalFileRef.current = importLocalFile;
  const handleDroppedFile = useCallback((file: File) => importLocalFileRef.current(file), []);
  const handleLostDrop = useCallback(
    () => setLoadFailure('The browser did not deliver the dropped file. Click the Chrome window first, then drag the file in again, or use the Open button.'),
    [],
  );
  const handleNoFile = useCallback(() => setLoadFailure('No file could be read from the drop.'), []);

  const downloadDatalog = async () => {
    if (!session) return;
    try {
      setNotice('Preparing the datalog…');
      const result = await datalogClient.exportRange({ start: session.startTime, end: session.endTime });
      downloadBlob(result.blob, session.fileName);
      setNotice('Datalog downloaded.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Datalog download failed.');
    }
  };

  // The Download Datalog button is hidden for now; keep these referenced until it returns.
  void [Download, downloadDatalog];

  const recentFileNames = [...new Set(Object.values(savedWorkspaces).sort((a, b) => b.savedAt - a.savedAt).map((item) => item.fileName))];

  const openChannels = () => {
    setChannelsOpen(true);
    setInspectorOpen(false);
  };

  const openInspector = () => {
    setInspectorOpen(true);
    setChannelsOpen(false);
  };

  const collapseChannels = () => {
    setChannelsOpen(false);
    setChannelsCollapsed(true);
  };

  const collapseInspector = () => {
    setInspectorOpen(false);
    setInspectorCollapsed(true);
  };

  return (
    <div className="app-root">
      <header className="top-bar">
        <div className="brand-block">
          <BanksLogo />
          <div className="brand-copy">
            <strong>{session ? session.fileName : 'iDash Datalog Analyzer'}</strong>
            <span>{session ? formatSessionSummary(session) : 'Private browser analysis'}</span>
          </div>
        </div>
        {session && (
          <div className="top-actions">
            <button
              className="secondary-button open-drop-button mobile-menu-button"
              onClick={() => inputRef.current?.click()}
              title="Open a CSV, or drag and drop one anywhere in this window"
            >
              <FolderOpen size={17} /> <span>Open</span>
              <span className="drop-chip">or drop a iDash CSV Log here</span>
            </button>
            {/* Download button hidden for now; downloadDatalog is kept so it can return.
            <div className="export-actions">
              <button className="secondary-button" onClick={() => void downloadDatalog()}>
                <Download size={17} /> <span>Download Datalog</span>
              </button>
            </div>
            */}
            <button className="primary-button" onClick={addGraph}>
              <Plus size={18} /> <span>Add Graph</span>
            </button>
          </div>
        )}
        {!session && (
          <div className="privacy-badge"><ShieldCheck size={16} /> Analysis stays in this browser</div>
        )}
        <input
          ref={inputRef}
          className="visually-hidden"
          type="file"
          accept=".csv,text/csv"
          aria-label="Open another iDash CSV"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) importLocalFile(file);
            event.currentTarget.value = '';
          }}
        />
      </header>

      {!session ? (
        <FileDropZone onFile={importLocalFile} recentFileNames={recentFileNames} />
      ) : (
        <div
          className={`analysis-layout${channelsCollapsed ? ' channels-collapsed' : ''}${inspectorCollapsed ? ' inspector-collapsed' : ''}${channelDragActive ? ' is-channel-dragging' : ''}`}
        >
          <aside className={`channels-column ${channelsOpen ? 'drawer-open' : ''}${channelsCollapsed ? ' is-collapsed' : ''}`}>
            <ChannelBrowser
              channels={session.channels}
              graphs={graphs}
              focusedGraphId={focusedGraphId}
              onAssign={assignChannel}
              onClose={collapseChannels}
              onDragActiveChange={setChannelDragActive}
              onNotice={setNotice}
            />
            <nav className="collapsed-sidebar" aria-label="Collapsed workspace sidebar">
              <button
                className="collapsed-sidebar-action is-active"
                onClick={() => setChannelsCollapsed(false)}
                aria-label="Expand channels"
                title="Expand channels"
              >
                <PanelLeft size={22} />
                <span>Channels</span>
              </button>
              <button
                className="collapsed-sidebar-action"
                onClick={() => {
                  setInspectorCollapsed(false);
                  openInspector();
                }}
                aria-label="Open inspector"
                title="Open inspector"
              >
                <BarChart3 size={22} />
                <span>Inspector</span>
              </button>
            </nav>
          </aside>
          {(channelsOpen || inspectorOpen) && (
            <button
              className="drawer-backdrop"
              onClick={() => {
                setChannelsOpen(false);
                setInspectorOpen(false);
              }}
              aria-label="Close open panel"
            />
          )}

          <main className="graph-workspace">
            <div className="workspace-toolbar">
              <div>
                <strong>Analysis workspace</strong>
                <span>Wheel or pinch to zoom · drag to pan</span>
              </div>
              <div>
                <button className="icon-text-button" disabled={!undoStack.length} onClick={undo}>
                  <Undo2 size={15} /> Undo
                </button>
                <button className="icon-text-button" onClick={resetWorkspace}>
                  <RotateCcw size={15} /> Reset
                </button>
              </div>
            </div>
            <div className="graph-stack">
              {graphs.map((graph) => (
                <GraphPanel key={graph.id} graph={graph} channels={session.channels} onNotice={setNotice} />
              ))}
            </div>
            <div className="graph-stack-actions">
              <button className="secondary-button add-graph-below" onClick={addGraph}>
                <Plus size={17} /> <span>Add Graph</span>
              </button>
            </div>
            <OverviewScrubber graphs={graphs} focusedGraphId={focusedGraphId} />
          </main>

          <aside className={`inspector-column ${inspectorOpen ? 'drawer-open' : ''}${inspectorCollapsed ? ' is-collapsed' : ''}`}>
            <Inspector channels={session.channels} onClose={collapseInspector} />
            <button
              className="panel-restore-button"
              onClick={() => setInspectorCollapsed(false)}
              aria-label="Expand inspector"
              title="Expand inspector"
            >
              <PanelRight size={20} />
            </button>
          </aside>

          <nav className="medium-panel-menu" aria-label="Workspace panels">
            <button onClick={openChannels} aria-expanded={channelsOpen}>
              <PanelLeft size={19} />
              <span>Channels</span>
            </button>
            <button onClick={openInspector} aria-expanded={inspectorOpen}>
              <BarChart3 size={19} />
              <span>Inspector</span>
            </button>
          </nav>

          <nav className="mobile-actions" aria-label="Workspace actions">
            <button onClick={addGraph}><Plus size={21} /><span>Add Graph</span></button>
            <button onClick={openChannels}><PanelLeft size={21} /><span>Channels</span></button>
            <button onClick={openInspector}><BarChart3 size={21} /><span>Cursor / Stats</span></button>
          </nav>
        </div>
      )}

      <FileDragOverlay showOverlay={Boolean(session)} onFile={handleDroppedFile} onNoFile={handleNoFile} onLostDrop={handleLostDrop} />

      {loading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="loading-card">
            <GaugeMark />
            <p className="eyebrow">{loadingMode === 'download' ? 'OPENING CLOUD DATALOG' : 'IMPORTING LOCALLY'}</p>
            <h2>{phase}</h2>
            <div className="progress-track"><span style={{ width: `${Math.max(3, progress * 100)}%` }} /></div>
            <p>
              {Math.round(progress * 100)}% · {loadingMode === 'download'
                ? 'downloading from the shared link'
                : 'processing in this browser'}
            </p>
            <button
              className="secondary-button"
              onClick={() => {
                if (cloudDownloadController.current) cloudDownloadController.current.abort();
                else datalogClient.cancelLoad();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loadFailure && (
        <div className="modal-overlay" role="presentation">
          <section className="mapping-dialog" role="alertdialog" aria-modal="true" aria-labelledby="load-failure-title">
            <button className="icon-button modal-close" onClick={() => setLoadFailure(null)} aria-label="Close">
              <X size={19} />
            </button>
            <h2 id="load-failure-title">File failed to load</h2>
            <p>Please try again.</p>
            <p>{loadFailure}</p>
            <button className="primary-button dialog-primary" onClick={() => setLoadFailure(null)}>
              OK
            </button>
          </section>
        </div>
      )}

      {timeMapping && (
        <div className="modal-overlay" role="presentation">
          <section className="mapping-dialog" role="dialog" aria-modal="true" aria-labelledby="mapping-title">
            <button className="icon-button modal-close" onClick={() => setTimeMapping(null)} aria-label="Close">
              <X size={19} />
            </button>
            <p className="eyebrow">IMPORT SETUP</p>
            <h2 id="mapping-title">Choose the elapsed-time column</h2>
            <p>The chart needs one numeric column for the synchronized horizontal axis.</p>
            <label>
              Time column
              <select
                value={selectedTimeColumn ?? ''}
                onChange={(event) => setSelectedTimeColumn(Number(event.currentTarget.value))}
              >
                {sortChannelsByName(timeMapping.candidates).map((channel) => (
                  <option key={channel.id} value={channel.sourceColumnIndex}>
                    {channel.displayName} {channel.unit ? `(${channel.unit})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="primary-button dialog-primary"
              disabled={selectedTimeColumn === null}
              onClick={() => {
                if (selectedTimeColumn !== null) void importFile(timeMapping.file, selectedTimeColumn);
              }}
            >
              Continue import
            </button>
          </section>
        </div>
      )}

      {error && (
        <div className="error-banner" role="alert">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error"><X size={17} /></button>
        </div>
      )}
      {notice && <div className="toast" role="status" aria-live="polite">{notice}</div>}
    </div>
  );
}
